import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  MenuItem,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Pagination,
  Stack,
  Tooltip,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { Visibility, Download, CheckCircle, Cancel, HourglassEmpty, Search, Refresh, AttachMoney, People, Edit, FileDownload, FilterList, Receipt, PictureAsPdf } from '@mui/icons-material';
import { adminAPI } from '../api/admin.api';
import athleteEventAPI from '../api/athleteEvent.api';
import api from '../api/axios';


const EventApplicationsList = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [downloadingInvoices, setDownloadingInvoices] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    stateAssociationId: '',
    eventId: '',
    startDate: '',
    endDate: '',
  });
  
  // Dropdown data
  const [stateAssociations, setStateAssociations] = useState([]);
  const [athleteEvents, setAthleteEvents] = useState([]);
  
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewData, setReviewData] = useState({
    action: 'approve',
    reviewNotes: '',
    modifiedAmount: '',
    amountModificationReason: '',
  });
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    fetchApplications();
    fetchStatistics();
  }, [page, filters.status, filters.stateAssociationId, filters.eventId, filters.startDate, filters.endDate]);

  useEffect(() => {
    fetchStateAssociations();
    fetchAthleteEvents();
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page,
        limit: 10,
        sortBy: 'submittedAt',
        sortOrder: 'desc',
      };
      
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.stateAssociationId) params.stateAssociationId = filters.stateAssociationId;
      if (filters.eventId) params.eventId = filters.eventId;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.search) params.search = filters.search;

      const response = await adminAPI.getAllEventApplications(params);
      setApplications(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUrl = (url) => {
    if (!url) return;
    window.open(url, '_blank');
  };

  const handleDownloadInvoice = (app) => {
    if (!app.invoicePath) return;
    handleOpenUrl(app.invoicePath);
  };

  const handleDownloadCertificate = (app) => {
    if (!app.participationCertificatePath) return;
    handleOpenUrl(app.participationCertificatePath);
  };

  const fetchStatistics = async () => {
    try {
      const params = {};
      if (filters.eventId) params.eventId = filters.eventId;
      
      const response = await adminAPI.getApplicationStatistics(params);
      setStatistics(response.data);
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    }
  };

  const fetchStateAssociations = async () => {
    try {
      const response = await adminAPI.getAllStateAssociations({ 
        limit: 1000,
        verificationStatus: 'approved'
      });
      setStateAssociations(response.data || []);
    } catch (err) {
      console.error('Failed to fetch state associations:', err);
    }
  };

  const fetchAthleteEvents = async () => {
    try {
      const response = await athleteEventAPI.getAllAthleteEvents({ limit: 1000 });
      setAthleteEvents(response.data || []);
    } catch (err) {
      console.error('Failed to fetch athlete events:', err);
    }
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const params = {};
      
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.stateAssociationId) params.stateAssociationId = filters.stateAssociationId;
      if (filters.eventId) params.eventId = filters.eventId;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.search) params.search = filters.search;

      const response = await adminAPI.exportEventApplications(params);
      
      // Create blob and download
      const blob = new Blob([response], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `event-applications-${Date.now()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess('CSV exported successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to export CSV');
    } finally {
      setExportLoading(false);
    }
  };

  const handleViewDetails = async (application) => {
    try {
      const response = await adminAPI.getEventApplicationById(application._id);
      setSelectedApplication(response.data);
      setDetailsDialogOpen(true);
    } catch (err) {
      setError('Failed to fetch application details');
    }
  };

  const handleDownloadFile = async (application) => {
    try {
      const response = await adminAPI.downloadApplicationFile(application._id);
      window.open(response.data.downloadUrl, '_blank');
      setSuccess('File download started');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to download file');
    }
  };

  const handleOpenReviewDialog = (application) => {
    setSelectedApplication(application);
    setReviewData({
      action: 'approve',
      reviewNotes: '',
      modifiedAmount: application.totalAmount?.toString() || '',
      amountModificationReason: '',
    });
    setReviewDialogOpen(true);
  };

  const handleReviewSubmit = async () => {
    if (!selectedApplication) return;

    try {
      setLoading(true);
      const data = {
        action: reviewData.action,
        reviewNotes: reviewData.reviewNotes,
      };

      if (reviewData.action === 'approve' && reviewData.modifiedAmount) {
        data.modifiedAmount = parseFloat(reviewData.modifiedAmount);
        data.amountModificationReason = reviewData.amountModificationReason;
      }

      await adminAPI.reviewEventApplication(selectedApplication._id, data);
      setSuccess(
        `Application ${
          reviewData.action === 'approve'
            ? 'approved'
            : reviewData.action === 'reject'
            ? 'rejected'
            : 'marked for review'
        } successfully`
      );
      setReviewDialogOpen(false);
      fetchApplications();
      fetchStatistics();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to review application');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoicesZip = async () => {
  try {
    setDownloadingInvoices(true);

    const params = {};
    if (filters.status !== 'all') params.status = filters.status;
    if (filters.stateAssociationId) params.stateAssociationId = filters.stateAssociationId;
    if (filters.eventId) params.eventId = filters.eventId;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.search) params.search = filters.search;

    // Use axios from admin.api.js
    const response = await api.get('/admin/event-applications/invoices/download', {
      params,
      responseType: 'blob', // Important for binary data
    });

    const blob = new Blob([response.data], { type: 'application/zip' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `event-app-invoices-${Date.now()}.zip`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);
    setSuccess('Invoices downloaded successfully');
    setTimeout(() => setSuccess(null), 3000);
  } catch (err) {
    console.error('Download error', err);
    setError(err.response?.data?.message || 'Failed to download invoices');
  } finally {
    setDownloadingInvoices(false);
  }
};

  const handleClearFilters = () => {
    setFilters({
      status: 'all',
      search: '',
      stateAssociationId: '',
      eventId: '',
      startDate: '',
      endDate: '',
    });
    setPage(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted':
        return 'info';
      case 'under_review':
        return 'warning';
      case 'payment_pending':
        return 'secondary';
      case 'payment_completed':
        return 'primary';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
      case 'payment_completed':
        return <CheckCircle />;
      case 'rejected':
        return <Cancel />;
      default:
        return <HourglassEmpty />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Event Applications Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review and manage state association event applications
        </Typography>
      </Box>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="primary">
                  {statistics.totalApplications}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Applications
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="warning.main">
                  {statistics.submittedApplications}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Review
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="success.main">
                  {statistics.approvedApplications}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Approved
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="primary">
                  ₹{(statistics.totalRevenue || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Revenue
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            <FilterList sx={{ mr: 1, verticalAlign: 'middle' }} />
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Status"
                value={filters.status}
                onChange={(e) => {
                  setFilters({ ...filters, status: e.target.value });
                  setPage(1);
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="submitted">Submitted</MenuItem>
                <MenuItem value="under_review">Under Review</MenuItem>
                <MenuItem value="payment_pending">Payment Pending</MenuItem>
                <MenuItem value="payment_completed">Payment Completed</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={stateAssociations}
                getOptionLabel={(option) =>
                  `${option.registrationNumber} - ${option.email}`
                }
                value={
                  stateAssociations.find((sa) => sa._id === filters.stateAssociationId) ||
                  null
                }
                onChange={(event, newValue) => {
                  setFilters({
                    ...filters,
                    stateAssociationId: newValue?._id || '',
                  });
                  setPage(1);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="State Association" />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={athleteEvents}
                getOptionLabel={(option) => `${option.title} - ${option.venue}`}
                value={
                  athleteEvents.find((event) => event._id === filters.eventId) || null
                }
                onChange={(event, newValue) => {
                  setFilters({ ...filters, eventId: newValue?._id || '' });
                  setPage(1);
                }}
                renderInput={(params) => <TextField {...params} label="Athlete Event" />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Search applications..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={filters.startDate}
                onChange={(e) => {
                  setFilters({ ...filters, startDate: e.target.value });
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={filters.endDate}
                onChange={(e) => {
                  setFilters({ ...filters, endDate: e.target.value });
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleClearFilters}
                sx={{ height: 56 }}
              >
                Clear Filters
              </Button>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                color="success"
                startIcon={<FileDownload />}
                onClick={handleExportCSV}
                disabled={exportLoading}
                sx={{ height: 56 }}
              >
                {exportLoading ? 'Exporting...' : 'Export CSV'}
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
  <Button
    fullWidth
    variant="outlined"
    color="info"
    startIcon={<Receipt />}
    onClick={handleDownloadInvoicesZip}
    disabled={downloadingInvoices}
    sx={{ height: 56 }}
  >
    {downloadingInvoices ? 'Downloading...' : 'Download Invoices ZIP'}
  </Button>
</Grid>

          </Grid>
        </CardContent>
      </Card>

      {/* Applications Table */}
      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Application ID</TableCell>
                <TableCell>State Association</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Participants</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : applications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No applications found
                  </TableCell>
                </TableRow>
              ) : (
                applications.map((app) => (
                  <TableRow key={app._id}>
                    <TableCell>{app._id.slice(-8)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {app.stateAssociation?.registrationNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {app.stateAssociation?.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{app.athleteEvent?.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {app.athleteEvent?.venue}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<People />}
                        label={app.totalParticipants}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        ₹{(app.finalAmount || app.totalAmount)?.toLocaleString()}
                      </Typography>
                      {app.adminModifiedAmount && (
                        <Chip
                          label="Modified"
                          size="small"
                          color="warning"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(app.applicationStatus)}
                        label={app.applicationStatus?.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(app.applicationStatus)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {app.submittedAt ? formatDate(app.submittedAt) : 'N/A'}
                    </TableCell>
                    <TableCell>
  <Tooltip title="View Details">
    <IconButton size="small" color="primary" onClick={() => handleViewDetails(app)}>
      <Visibility fontSize="small" />
    </IconButton>
  </Tooltip>

  <Tooltip title="Download Application File">
    <IconButton size="small" color="info" onClick={() => handleDownloadFile(app)}>
      <Download fontSize="small" />
    </IconButton>
  </Tooltip>

  {/* NEW: Invoice download (only when payment completed & invoicePath exists) */}
  {app.paymentStatus === 'completed' && app.invoicePath && (
    <Tooltip title="Download Invoice">
      <IconButton size="small" color="info" onClick={() => handleDownloadInvoice(app)}>
        <Receipt fontSize="small" />
      </IconButton>
    </Tooltip>
  )}

  {/* NEW: Participation certificate download */}
  {app.paymentStatus === 'completed' && app.participationCertificatePath && (
    <Tooltip title="Download Certificate">
      <IconButton size="small" color="success" onClick={() => handleDownloadCertificate(app)}>
        <PictureAsPdf fontSize="small" />
      </IconButton>
    </Tooltip>
  )}

  {app.applicationStatus === 'submitted' && (
    <Tooltip title="Review Application">
      <IconButton size="small" color="success" onClick={() => handleOpenReviewDialog(app)}>
        <Edit fontSize="small" />
      </IconButton>
    </Tooltip>
  )}
</TableCell>

                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Application Details</DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Application ID
                  </Typography>
                  <Typography variant="body1">{selectedApplication._id}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    icon={getStatusIcon(selectedApplication.applicationStatus)}
                    label={selectedApplication.applicationStatus
                      ?.replace('_', ' ')
                      .toUpperCase()}
                    color={getStatusColor(selectedApplication.applicationStatus)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Event
                  </Typography>
                  <Typography variant="body1">
                    {selectedApplication.athleteEvent?.title}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    State Association
                  </Typography>
                  <Typography variant="body1">
                    {selectedApplication.stateAssociation?.registrationNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Participants
                  </Typography>
                  <Typography variant="body1">
                    {selectedApplication.totalParticipants}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Amount
                  </Typography>
                  <Typography variant="body1">
                    ₹{selectedApplication.totalAmount?.toLocaleString()}
                  </Typography>
                </Grid>
                {selectedApplication.reviewNotes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Review Notes
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.reviewNotes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Review Application</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Action"
            value={reviewData.action}
            onChange={(e) => setReviewData({ ...reviewData, action: e.target.value })}
            sx={{ mb: 2, mt: 2 }}
          >
            <MenuItem value="approve">Approve</MenuItem>
            <MenuItem value="reject">Reject</MenuItem>
            <MenuItem value="under_review">Mark for Review</MenuItem>
          </TextField>

          {reviewData.action === 'approve' && (
            <>
              <TextField
                fullWidth
                type="number"
                label="Modified Amount"
                value={reviewData.modifiedAmount}
                onChange={(e) =>
                  setReviewData({ ...reviewData, modifiedAmount: e.target.value })
                }
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={{ mb: 2 }}
                helperText="Modify amount if needed"
              />
              {reviewData.modifiedAmount &&
                selectedApplication &&
                parseFloat(reviewData.modifiedAmount) !==
                  selectedApplication.totalAmount && (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Amount Modification Reason"
                    value={reviewData.amountModificationReason}
                    onChange={(e) =>
                      setReviewData({
                        ...reviewData,
                        amountModificationReason: e.target.value,
                      })
                    }
                    sx={{ mb: 2 }}
                    required
                  />
                )}
            </>
          )}

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Review Notes"
            value={reviewData.reviewNotes}
            onChange={(e) =>
              setReviewData({ ...reviewData, reviewNotes: e.target.value })
            }
            placeholder="Add any notes or comments..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReviewSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Submit Review'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EventApplicationsList;
